/**
 * A run's open decision draft: the answerable ask for a plan approval or a
 * mid-run tool grant that has no work-item gate. The same draft record the
 * Decisions surface resolves, answered through the same `/api/decisions`
 * routes, never a fork. Lifted from the case file whole.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { DecisionDraft } from "@yagni/shared";
import { DecisionCard, type DecisionIntent } from "../decisions/DecisionCard.js";
import { EXECUTING_RECEIPT, HANDOFF_FLIP_MS } from "../decisions/consequence.js";
import { captureUiError } from "../sentry.js";

export function useOpenRunDecision(runIds: readonly string[]): { decision: DecisionDraft | null; reload: () => void } {
  const [decision, setDecision] = useState<DecisionDraft | null>(null);
  const key = runIds.join(",");
  const reload = useCallback(() => {
    if (!key) {
      setDecision(null);
      return;
    }
    const ids = key.split(",");
    fetch("/api/decisions", { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`decisions read failed (${r.status})`);
        return r.json() as Promise<{ decisions?: DecisionDraft[] }>;
      })
      .then((data) => {
        const match = (data.decisions ?? []).find((d) => {
          const runId = (d.primaryAction as { runId?: unknown } | null)?.runId;
          return typeof runId === "string" && ids.includes(runId);
        });
        setDecision(match ?? null);
      })
      .catch((err: unknown) => {
        captureUiError(err, { area: "Work", operation: "read_run_decision" });
        setDecision(null);
      });
  }, [key]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { decision, reload };
}

export function RunDecisionPanel({ decision, onResolved }: { decision: DecisionDraft; onResolved: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [handedOff, setHandedOff] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current != null) clearTimeout(timer.current);
    },
    [],
  );
  const isGrant = (decision.primaryAction as { type?: unknown } | null)?.type === "grant_skill";
  const receipt = isGrant ? "Granted. The run resumes." : EXECUTING_RECEIPT;

  const act = async (intent: DecisionIntent) => {
    setError(null);
    try {
      const res =
        intent === "approve"
          ? await fetch(`/api/decisions/${encodeURIComponent(decision.id)}/action`, {
              method: "POST",
              credentials: "include",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ action: "primary" }),
            })
          : await fetch(`/api/decisions/${encodeURIComponent(decision.id)}/dismiss`, {
              method: "POST",
              credentials: "include",
            });
      if (!res.ok) throw new Error(`Could not resolve this decision (${res.status})`);
      if (intent === "approve") {
        setHandedOff(receipt);
        timer.current = setTimeout(onResolved, HANDOFF_FLIP_MS);
      } else {
        onResolved();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resolve this decision.");
    }
  };

  return (
    <div className="wk-gate" data-testid="wk-run-decision">
      <DecisionCard
        title={decision.title}
        description={decision.body}
        proposal={null}
        verbLabel={isGrant ? "Grant the tool" : "Approve plan"}
        effect={isGrant ? "Granting resumes the run with this tool allowed." : "Approving starts execution within this plan."}
        declineLabel="Dismiss"
        resolved={handedOff != null}
        receiptText={handedOff ?? undefined}
        onResolve={(intent) => act(intent)}
        fieldIdPrefix={`run-decision-${decision.id}`}
        testIds={{
          verb: "wk-run-decision-verb",
          decline: "wk-run-decision-decline",
          effect: "wk-run-decision-effect",
          receipt: "wk-run-decision-receipt",
        }}
      />
      {error && (
        <p className="wk-gate-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
